import type { Vec2, Segment, AABB, Ring } from '../geo/types';
import type { CollisionWorld } from './types';
import { createSpatialHash, type SpatialHash } from '../geo/spatialHash';
import { ringBounds } from '../geo/polygon';
import { NAV_EPSILON, closestPointOnSegment, sweepCircle, type SegmentHit } from './sweep';

const MAX_SLIDE_ITERATIONS = 3;
const SEGMENT_CELL_M = 16;
const LOCAL_SEARCH_RADII = 10;
const SETTLE_ROUNDS = 4;

interface Boundaries {
  readonly hash: SpatialHash<Segment>;
  readonly all: readonly Segment[];
}

/** A region a circle may move through: its edges, and whether a bare point lies in it. */
export interface SlidingRegion {
  readonly radius: number;
  readonly rings: readonly Ring[];
  readonly open: (p: Vec2) => boolean;
}

function ringSegments(ring: Ring): Segment[] {
  return ring.map((a, i) => ({ a, b: ring[(i + 1) % ring.length]! }));
}

function indexBoundaries(rings: readonly Ring[]): Boundaries {
  const hash = createSpatialHash<Segment>(SEGMENT_CELL_M);
  const all = rings.flatMap(ringSegments);
  for (const seg of all) {
    hash.insert(seg, ringBounds([seg.a, seg.b]));
  }
  return { hash, all };
}

export function boxAround(p: Vec2, reach: number): AABB {
  return { minX: p[0] - reach, minY: p[1] - reach, maxX: p[0] + reach, maxY: p[1] + reach };
}

function nearestWithin(p: Vec2, segments: readonly Segment[], reach: number): SegmentHit[] {
  return segments
    .map((seg) => closestPointOnSegment(p, seg))
    .filter((hit) => hit.distance <= reach)
    .sort((x, y) => x.distance - y.distance);
}

function pushedClear(p: Vec2, radius: number, boundaries: Boundaries): Vec2 | null {
  const reach = radius + NAV_EPSILON;
  const close = boundaries.hash
    .query(boxAround(p, reach))
    .map((seg) => closestPointOnSegment(p, seg))
    .filter((hit) => hit.distance < radius);
  if (close.length === 0) return null;
  return close.reduce<Vec2>(
    (q, hit) => [
      q[0] + hit.normal[0] * (reach - hit.distance),
      q[1] + hit.normal[1] * (reach - hit.distance),
    ],
    p
  );
}

// A point pushed off one side of a corner still lies within the radius of the other side, so it is
// pushed clear of every boundary around it for a few rounds.
function settle(candidate: Vec2, radius: number, boundaries: Boundaries): Vec2 {
  let settled = candidate;
  for (let round = 0; round < SETTLE_ROUNDS; round++) {
    const next = pushedClear(settled, radius, boundaries);
    if (!next) return settled;
    settled = next;
  }
  return settled;
}

// PLAN §5 nearestWalkable: the nearest boundary point, pushed just past the radius on its open side.
function firstWalkable(
  hits: readonly SegmentHit[],
  radius: number,
  boundaries: Boundaries,
  contains: (p: Vec2) => boolean
): Vec2 | null {
  const push = radius + NAV_EPSILON;
  for (const { point, normal } of hits) {
    for (const side of [1, -1]) {
      const pushed: Vec2 = [point[0] + normal[0] * side * push, point[1] + normal[1] * side * push];
      const candidate = settle(pushed, radius, boundaries);
      if (contains(candidate)) return candidate;
    }
  }
  return null;
}

/** A circle that slides along the edges of its region, on land or on the water alike. */
export function createSlidingWorld({ radius, rings, open }: SlidingRegion): CollisionWorld {
  const boundaries = indexBoundaries(rings);

  function clearOfBoundaries(p: Vec2): boolean {
    const reach = radius + NAV_EPSILON;
    return boundaries.hash
      .query(boxAround(p, reach))
      .every((seg) => closestPointOnSegment(p, seg).distance >= radius - NAV_EPSILON);
  }

  function contains(p: Vec2): boolean {
    return open(p) && clearOfBoundaries(p);
  }

  // A point far from every boundary (the open IJ) falls back to a scan of all of them.
  function nearestWalkable(p: Vec2): Vec2 {
    if (contains(p)) return p;
    const reach = radius * LOCAL_SEARCH_RADII;
    const local = nearestWithin(p, boundaries.hash.query(boxAround(p, reach)), reach);
    return (
      firstWalkable(local, radius, boundaries, contains) ??
      firstWalkable(nearestWithin(p, boundaries.all, Infinity), radius, boundaries, contains) ??
      p
    );
  }

  // The nearest edge the circle lies against and is moving into.
  function touching(p: Vec2, delta: Vec2): SegmentHit | null {
    const reach = radius + 2 * NAV_EPSILON;
    return (
      nearestWithin(p, boundaries.hash.query(boxAround(p, reach)), reach).find(
        (hit) => delta[0] * hit.normal[0] + delta[1] * hit.normal[1] < -NAV_EPSILON
      ) ?? null
    );
  }

  function move(from: Vec2, delta: Vec2): Vec2 {
    let current: Vec2 = [from[0], from[1]];
    let remaining: Vec2 = [delta[0], delta[1]];

    for (let iter = 0; iter < MAX_SLIDE_ITERATIONS; iter++) {
      if (Math.hypot(remaining[0], remaining[1]) < NAV_EPSILON) break;
      const target: Vec2 = [current[0] + remaining[0], current[1] + remaining[1]];

      // Sweep before trusting the target: a walkable far bank must not let a long step jump the water.
      const hit = sweepCircle(current, remaining, radius, boundaries.hash);
      if (!hit) {
        if (contains(target)) return target;
        // A circle already lying against an edge has no first contact ahead of it to sweep to:
        // the rest of its step runs along that edge.
        const edge = touching(current, remaining);
        if (!edge) return contains(current) ? current : nearestWalkable(current);
        const along = remaining[0] * edge.tangent[0] + remaining[1] * edge.tangent[1];
        remaining = [edge.tangent[0] * along, edge.tangent[1] * along];
        continue;
      }

      // PLAN §5: stop just short of the contact, then slide the rest along the boundary.
      current = [
        hit.center[0] + hit.normal[0] * NAV_EPSILON,
        hit.center[1] + hit.normal[1] * NAV_EPSILON,
      ];
      const slide =
        (target[0] - hit.center[0]) * hit.tangent[0] + (target[1] - hit.center[1]) * hit.tangent[1];
      remaining = [hit.tangent[0] * slide, hit.tangent[1] * slide];
    }

    return contains(current) ? current : nearestWalkable(current);
  }

  return { radius, contains, move, nearestWalkable };
}
